import React from "react";

const ObsessiveCompulsiveTestForm = ({ formData, handleChange }) => {
    const items = [
        { id: "obsTime", title: "1. How much time do you spend on obsessive thoughts?", options: ["None", "0-1 hrs/day", "1-3 hrs/day", "3-8 hrs/day", "More than 8 hrs/day"] },
        { id: "obsInterference", title: "2. How much do your obsessive thoughts interfere with your personal, social, or work life?", options: ["None", "Mild", "Definite but manageable", "Substantial interference", "Severe"] },
        { id: "obsDistress", title: "3. How much do your obsessive thoughts distress you?", options: ["None", "Little", "Moderate but manageable", "Severe", "Nearly constant, Disabling"] },
        { id: "obsResistance", title: "4. How hard do you try to resist your obsessions?", options: ["Always try", "Try much of the time", "Try some of the time", "Rarely try. Often yield", "Never try. Completely yield"] },
        { id: "obsControl", title: "5. How much control do you have over your obsessive thoughts?", options: ["Complete control", "Much control", "Some control", "Little control", "No control"] },
        { id: "compTime", title: "6. How much time do you spend performing compulsive behaviors?", options: ["None", "0-1 hrs/day", "1-3 hrs/day", "3-8 hrs/day", "More than 8 hrs/day"] },
        { id: "compInterference", title: "7. How much do your compulsive behaviors interfere with your personal, social, or work life?", options: ["None", "Mild", "Definite but manageable", "Substantial interference", "Severe"] },
        { id: "compAnxiety", title: "8. How anxious would you feel if you were prevented from performing your compulsive behaviors?", options: ["None", "Little", "Moderate but manageable", "Severe", "Nearly constant, Disabling"] },
        { id: "compResistance", title: "9. How hard do you try to resist your compulsive behaviors?", options: ["Always try", "Try much of the time", "Try some of the time", "Rarely try. Often yield", "Never try. Completely yield"] },
        { id: "compControl", title: "10. How much control do you have over your compulsive behaviors?", options: ["Complete control", "Much control", "Some control", "Little control", "No control"] }
    ];

    const calculateTotal = () => {
        return items.reduce((sum, item) => sum + (parseInt(formData[item.id] || 0, 10)), 0);
    };

    const totalScore = calculateTotal();

    const getSeverity = (score) => {
        if (score <= 7) return { text: "Subclinical", color: "success" };
        if (score <= 15) return { text: "Mild OCD", color: "info" };
        if (score <= 23) return { text: "Moderate OCD", color: "warning" };
        if (score <= 31) return { text: "Severe OCD", color: "orange" };
        return { text: "Extreme OCD", color: "danger" };
    };

    const handleOptionChange = (id, index) => {
        const selectedItem = items.find(item => item.id === id);
        const valueText = selectedItem.options[index] || "";

        handleChange({
            target: {
                name: id,
                value: index // numeric score for calculation
            }
        });

        handleChange({
            target: {
                name: `${id}Value`,
                value: valueText // text for backend
            }
        });
    };

    const severity = getSeverity(totalScore);

    return (
        <>
            {/* Sticky Guidelines Button */}
            <div className="row mb-4 position-sticky top-0 bg-white pt-3" style={{ zIndex: 10 }}>
                <div className="col-md-3">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm w-100"
                        data-bs-toggle="modal"
                        data-bs-target="#ocdGuidelines"
                    >
                        <i className="ri-information-line me-2"></i>
                        YBOCS Guidelines & Scoring
                    </button>
                </div>
            </div>
            <div className="p-3" style={{ maxHeight: "60vh", overflowY: "auto", scrollbarWidth: "thin" }}>

                <div className="accordion" id="ocdAccordion">
                    {/* Client Information */}
                    <div className="accordion-item mb-3 shadow-sm rounded">
                        <h2 className="accordion-header">
                            <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#clientInfo">
                                Client Information
                            </button>
                        </h2>
                        <div id="clientInfo" className="accordion-collapse collapse show">
                            <div className="accordion-body pt-3">
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="clientName"
                                                value={formData.clientName || ""}
                                                onChange={handleChange}
                                                readOnly={!!formData.patientId} // ⭐ ADD THIS PROPERTY ⭐
                                            />
                                            <label>Client Name</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name="diagnosis" value={formData.diagnosis || ""} onChange={handleChange} />
                                            <label>Diagnosis</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name="assessedBy" value={formData.assessedBy || ""} onChange={handleChange} />
                                            <label>Assessed By</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Obsessions Section */}
                    <div className="accordion-item mb-4 shadow-sm rounded">
                        <h2 className="accordion-header">
                            <button className="accordion-button collapsed fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#obsessionsSection">
                                Obsessions (Questions 1–5)
                            </button>
                        </h2>
                        <div id="obsessionsSection" className="accordion-collapse collapse show">
                            <div className="accordion-body">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-4">
                                    Obsessions are frequent, unwelcome, and intrusive thoughts.
                                </small>

                                {items.slice(0, 5).map((item) => (
                                    <div className="mb-4 pb-3 border-bottom" key={item.id}>
                                        <div className="fw-bold text-secondary mb-3">{item.title}</div>
                                        <div className="row g-4">
                                            {item.options.map((opt, idx) => (
                                                <div className="col-lg-4" key={idx}>
                                                    <div className="form-check mb-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name={item.id}
                                                            id={`${item.id}_opt${idx}`}
                                                            value={idx}
                                                            checked={parseInt(formData[item.id] || 0, 10) === idx}
                                                            onChange={() => handleOptionChange(item.id, idx)}
                                                        />
                                                        <label className="form-check-label fw-medium d-flex align-items-start" htmlFor={`${item.id}_opt${idx}`}>
                                                            <span className="text-primary fw-bold me-3 flex-shrink-0">{idx}</span>
                                                            <span>{opt}</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Compulsions Section */}
                    <div className="accordion-item mb-5 shadow-sm rounded">
                        <h2 className="accordion-header">
                            <button className="accordion-button collapsed fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#compulsionsSection">
                                Compulsions (Questions 6–10)
                            </button>
                        </h2>
                        <div id="compulsionsSection" className="accordion-collapse collapse show">
                            <div className="accordion-body">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-4">
                                    Compulsions are repetitive behaviors or mental acts that you have a strong urge to repeat that are aimed at reducing your anxiety or preventing some dreaded event.
                                </small>

                                {items.slice(5).map((item) => (
                                    <div className="mb-4 pb-3 border-bottom" key={item.id}>
                                        <div className="fw-bold text-secondary mb-3">{item.title}</div>
                                        <div className="row g-4">
                                            {item.options.map((opt, idx) => (
                                                <div className="col-lg-4" key={idx}>
                                                    <div className="form-check mb-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name={item.id}
                                                            id={`${item.id}_opt${idx}`}
                                                            value={idx}
                                                            checked={parseInt(formData[item.id] || 0, 10) === idx}
                                                            onChange={() => handleOptionChange(item.id, idx)}
                                                        />
                                                        <label className="form-check-label fw-medium d-flex align-items-start" htmlFor={`${item.id}_opt${idx}`}>
                                                            <span className="text-primary fw-bold me-3 flex-shrink-0">{idx}</span>
                                                            <span>{opt}</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Guidelines Modal */}
                <div className="modal fade" id="ocdGuidelines" tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-body">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-4">
                                    Scoring:
                                </small>
                                <p className="small mb-3">Each item is rated from 0 (no symptoms) to 4 (extreme symptoms).</p>

                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-4">
                                    Interpretation:
                                </small>
                                <ul className="small mb-4">
                                    <li><strong>8–15</strong> → Mild OCD</li>
                                    <li><strong>16–23</strong> → Moderate OCD</li>
                                    <li><strong>24–31</strong> → Severe OCD</li>
                                    <li><strong>32–40</strong> → Extreme OCD</li>
                                </ul>

                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ObsessiveCompulsiveTestForm;